import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { MaskedLine, FadeUp } from "@/components/MaskedReveal";

const EASE = [0.76, 0, 0.24, 1];

export default function TrainingDetail({ config }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <>
      <Nav />
      <main className="bg-[#050505] text-white">
        {/* HERO */}
        <section
          ref={ref}
          className="relative h-[85svh] w-full overflow-hidden"
          data-testid={`training-hero-${config.slug}`}
        >
          <motion.div style={{ y }} className="absolute inset-0 w-full h-[130%] -top-[15%]">
            <img
              src={config.heroImage}
              alt={config.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 hero-vignette" />
          </motion.div>

          <motion.div
            style={{ y: heroTextY }}
            className="relative z-10 h-full flex flex-col justify-between mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 pt-32 pb-14"
          >
            <div className="flex items-start justify-between">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 1, ease: EASE }}
                className="font-mono-label text-white/60"
              >
                <span className="text-[#FF5A00]">◉</span> Training · {config.category}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 1, ease: EASE }}
                className="hidden md:block font-mono-label text-white/60 text-right"
              >
                Program Code · {config.code}
              </motion.div>
            </div>

            <div className="flex-1 flex flex-col justify-end">
              <h1 className="font-display uppercase leading-[0.86] tracking-[-0.04em] text-[12vw] md:text-[8.5vw]">
                <MaskedLine delay={0.4} animate={{ y: "0%" }}>
                  {config.line1}
                </MaskedLine>
                <MaskedLine
                  delay={0.6}
                  animate={{ y: "0%" }}
                  className={config.line2Italic ? "pl-[6vw] md:pl-[10vw]" : ""}
                >
                  {config.line2Italic && (
                    <span className="font-serif-italic italic pr-4 text-[0.85em]">
                      {config.line2Italic}
                    </span>
                  )}
                  {config.line2}
                </MaskedLine>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 1, ease: EASE }}
                className="mt-8 md:mt-12 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
              >
                {config.subtitle}
              </motion.p>
            </div>

            <div className="flex items-end justify-between font-mono-label text-white/50 text-[10px]">
              <Link
                to="/"
                className="hover:text-[#FF5A00] transition-colors duration-500"
                data-testid={`training-back-${config.slug}`}
              >
                ← Back to El Elyon Air
              </Link>
              <div className="hidden md:block">Scroll — Curriculum</div>
              <div>{config.code}</div>
            </div>
          </motion.div>
        </section>

        {/* INSURANCE BADGE (conditional) */}
        {config.insurance && (
          <section
            className="relative border-y border-[#FF5A00]/40 bg-[#FF5A00]/[0.06] py-10 md:py-14"
            data-testid={`insurance-banner-${config.slug}`}
          >
            <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-3 flex items-center gap-4">
                <div className="w-14 h-14 border border-[#FF5A00] flex items-center justify-center">
                  <span className="font-serif-italic text-[#FF5A00] text-2xl">✦</span>
                </div>
                <div>
                  <div className="font-mono-label text-[#FF5A00]">Insurance-Approved</div>
                  <div className="font-serif-italic text-white/85 text-lg leading-none mt-1">
                    In-Aircraft Training
                  </div>
                </div>
              </div>
              <div className="md:col-span-9">
                <p className="text-white/85 text-base md:text-lg leading-relaxed">
                  <span className="font-display uppercase tracking-tight text-white">El Elyon Air</span>{" "}
                  provides <span className="text-white font-medium">in-aircraft initial and recurrent C408 training</span>{" "}
                  approved through <span className="text-white font-medium">W. Brown &amp; Associates</span> —
                  the leading aviation insurance underwriter for King Air and turbine aircraft operators.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* OVERVIEW */}
        <section className="py-24 md:py-40 border-t border-white/10">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Overview · 001
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.9]">
                <MaskedLine>The Aircraft.</MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-6">
              {config.overview.map((p, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <p className="text-white/70 text-base md:text-lg leading-relaxed">{p}</p>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>

        {/* SPEC STRIP */}
        <section className="py-16 md:py-24 border-y border-white/10 bg-[#0A0A0C]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {config.specs.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                className="border-l border-white/15 pl-5"
                data-testid={`spec-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="font-mono-label text-white/40 mb-2">{s.label}</div>
                <div className="font-display text-2xl md:text-4xl uppercase leading-none">
                  {s.value}
                </div>
                {s.unit && (
                  <div className="font-serif-italic text-white/50 text-sm mt-1">{s.unit}</div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* CURRICULUM */}
        <section className="py-24 md:py-40">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
              <div className="md:col-span-6">
                <FadeUp>
                  <div className="font-mono-label text-white/50 mb-6">
                    <span className="text-[#FF5A00]">◉</span> Curriculum · Modules
                  </div>
                </FadeUp>
                <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight">
                  <MaskedLine>The Syllabus.</MaskedLine>
                </h2>
              </div>
              <div className="md:col-span-5 md:col-start-8 flex items-end">
                <FadeUp delay={0.15}>
                  <p className="text-white/60 text-base md:text-lg leading-relaxed">
                    {config.curriculumIntro}
                  </p>
                </FadeUp>
              </div>
            </div>

            <div className="border-t border-white/10">
              {config.modules.map((m, i) => (
                <motion.div
                  key={m.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.08 }}
                  className="grid grid-cols-12 gap-6 items-start py-10 border-b border-white/10"
                  data-testid={`module-${i}`}
                >
                  <div className="col-span-3 md:col-span-2 font-serif-italic text-white/45 text-3xl md:text-5xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-9 md:col-span-5">
                    <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">
                      {m.title}
                    </h3>
                  </div>
                  <div className="col-span-12 md:col-span-5 text-white/60 text-sm md:text-[15px] leading-relaxed">
                    {m.body}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="py-24 md:py-40 border-t border-white/10 bg-[#0A0A0C]">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <FadeUp>
                <div className="font-mono-label text-white/50 mb-6">
                  <span className="text-[#FF5A00]">◉</span> Enrollment · Fit
                </div>
              </FadeUp>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight">
                <MaskedLine>Who</MaskedLine>
                <MaskedLine delay={0.1}>this is for.</MaskedLine>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-5">
              {config.audience.map((a, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
                  transition={{ duration: 0.9, ease: EASE, delay: i * 0.08 }}
                  className="flex items-start gap-5 border-l border-white/15 pl-5"
                >
                  <span className="font-mono-label text-[#FF5A00] mt-1">0{i + 1}</span>
                  <p className="text-white/75 text-base md:text-lg leading-relaxed">{a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 md:py-40">
          <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-8">
                <FadeUp>
                  <div className="font-mono-label text-white/50 mb-6">
                    <span className="text-[#FF5A00]">◉</span> Reserve · Seat
                  </div>
                </FadeUp>
                <h2 className="font-display uppercase leading-[0.84] tracking-tighter text-[14vw] md:text-[9vw]">
                  <MaskedLine>Book</MaskedLine>
                  <MaskedLine delay={0.12}>your seat.</MaskedLine>
                </h2>
              </div>
              <div className="md:col-span-4 md:col-start-9 flex flex-col gap-4">
                <FadeUp delay={0.15}>
                  <p className="text-white/60 text-base md:text-lg leading-relaxed mb-6">
                    Cohorts are intentionally small. Reach out with your certification level,
                    target date, and program preference — we&apos;ll respond personally.
                  </p>
                </FadeUp>
                <Link
                  to="/#contact"
                  className="btn-outline btn-solid"
                  data-testid={`training-cta-${config.slug}`}
                >
                  Request Enrollment
                  <span aria-hidden>→</span>
                </Link>
                <Link
                  to="/"
                  className="btn-outline"
                  data-testid={`training-cta-home-${config.slug}`}
                >
                  <span aria-hidden>←</span>
                  El Elyon Air · Home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
